import { Invites } from './../../invites/components/invites';
import { Subject, BehaviorSubject } from 'rxjs';
import { TableSides } from './types';
import SoundProvider from './soundProvider';
import BufferReader from '../../socket/bufferReader';
import { Message } from '@base/features/chat/message';
import { MessagesService } from '@base/features/chat/messagesService';
import { IPlayer, IPlayersOnlineRepository } from '@base/features/player/model/interfaces';
import { BasePreferences, IPreferencesService } from '../../preferences/model/types';
import { ReactNode } from 'react';
import { IModalProvider } from '@base/features/modal/model/interfaces';
import BaseTable from './baseTable';
import LobbyService from '@base/features/lobby/model/lobbyService';
import Color from '@base/features/game/model/color';
import { ISocketClient } from '@base/features/socket/interfaces';
import { CreateTableSettings } from '@base/features/table/interfaces';

export interface OpponentIdleActionsDialog {
  visible: BehaviorSubject<boolean>;
  title: BehaviorSubject<string>;
  message: BehaviorSubject<string>;
  buttons: BehaviorSubject<ModalButton[]>;
  sendService(type: any, pid: number, tid: number): void;
  show: (playmode: boolean, showControls: boolean) => void;
  hide: () => void;
  setTime: (elapsedTime: number) => void;
  end: () => void;
  wait: () => void;
}

export interface IGameProvider<P extends BasePreferences = BasePreferences, T extends ITable = BaseTable> {
  GAME_ID: number;
  APP_ID: string;
  APP_NAME: string;
  DEFAULT_TABLE_SETTINGS: CreateTableSettings;
  locales: string[];
  createGameState(
    table: T,
    tablesRepository: ITableRepository,
    playersOnlineRepository: IPlayersOnlineRepository,
    lobbyService: LobbyService,
    soundProvider: SoundProvider<P>,
    preferencesService: IPreferencesService<P>,
    messagesService: MessagesService<P, T>,
    modalProvider: IModalProvider,
    gameService: IGameService,
    socket: ISocketClient,
  ): BaseGameState;
  getGameBoardView: () => JSX.Element;
  getAdditionalPreferences: () => { title: string; icon: JSX.Element; content: JSX.Element }[] | null;
  getGamePreferences: () => {title: string, key: string}[];
  parseTournamentSettings: (settings: [string, string, string]) => [number, number, number];
  getHelpPage: () => JSX.Element;
}

export interface IGameService {
  winnerName: Subject<string>;
  isTournamentWinnerAnimation: BehaviorSubject<boolean>;
  gameData: {
    reconnectionTime: number;
  };

  sendMove(move: string, tid: number, time: number): void;
  sendPlay(playCommand: any, pid?: number, tid?: number): void;
  sendDice(pid: number, tid: number): void;
  sendCommand(pid: number, tid: number, cmd: number, command: string): void;
  onGotGame: (action: any) => void;
  onGotGameProgress(progress: GameProgress): void;
  onGotStringCommand(type: number, command: string): void;
  onGotCommand: (type: number, pid: number, tid: number, par: number) => void;
  onGotDeal(deal: any): void;
  onGotScore(scores: any): void;
  onGotWin(action: number, pids: number[], tid: number, reason: number): void;
}

export interface GameData {
  reconnectionTime: number;
}

export interface ITableRepository<T extends ITable = BaseTable> {
  tables: BehaviorSubject<T[]>;
  add(table: TableOptions): void;
  cleanTables(): void;
  delete(id: number): void;
  getTable(id: number): ITable | undefined;
  updatePlayerOnTable(tableId: number, seat: number, player?: IPlayer): void;
  updateDataTable(tableOptions: TableOptions): ITable;
  checkParams(params: any, options?: any, me?: IPlayer): string | undefined;
}

/* export interface ISocketClient { */
/*   connect(proxy: string[], host: string, port: number): void; */
/*   send(dataArray: any): void; */
/*   sendPlay(playCommand: any, pid?: number, tid?: number): void; */
/*   sendInvite(pid: number, tid: number, inviteType: any): void; */
/*   closeConnection(): void; */
/*   private onReceiveGameProgress(data: BufferReader): void; */
/*   connectInRoom(urls: string[], host: string, port: number, type: string | undefined): void; */
/*   sendJoinTable(tid: number, joinMode: number): void; */
/*   sendFingerPid(pid: number): void; */
/*   sendMove(move: string, tid: number, time: number): void; */
/*   sendTableOp(pid?: number, tid?: number, mode?: number): void; */
/*   sendDice(pid: number, tid: number): void; */
/*   onPlayerAwayChanged(isAway: boolean): void; */
/*   private onReceiveTableOwn(data: BufferReader): void; */
/*   private onReceiveDeal(data: BufferReader): void; */
/*   private onReceiveScommand(data: BufferReader): void; */
/*   sendAlive(mode: number): void; */
/*   sendTable(tid: number, options: CreateTableSettings): void; */
/*   sendService(type: any, pid: number, tid: number): void; */
/*   sendMessage(pid: number, text: string, mode: number): void; */
/*   sendCommand(pid: number, tid: number, cmd: number, command: string): void; */
/*   getDefaultConfiguration(): ConnectionConfig; */
/* } */

export type OnPacketType = (packet: BufferReader) => void;

export type PlayerJoinedType = {
  pid: number;
  name?: string;
  rating?: number;
  status?: number;
  join?: boolean;
  away?: boolean;
  avatar?: number;
  gender?: number;
  id?: number;
  rcLeft?: number;
};

export type PlayerOptions = {
  pid: number;
  data: {
    userId: string;
    status: number;
    lastLogin: number;
    onlineTime: number;
    realLevel: number;
    registration: number;
    nGames: number;
    nWin: number;
    nLost: number;
    nDraw: number;
    avgOppRating: number;
    rating: number;
    maxRating: number;
    nMt: number;
    nMtWin: number;
    nTr: number;
    nTr1: number;
    nTr2: number;
    nTr3: number;
    ladderPos: number;
  };
};

export type TableOptions = {
  tid: number;
  inviteOnly: boolean;
  timeGame: number;
  timeGameAdd: number;
  ratingGame: boolean;
  cost: number;
  reserved: number;
  gameColor: number;
  withTime: boolean;
  timePerMove: number;
  withTimePerMove: boolean;
  maxPoints: number;
  minPoints: number;
  ladder: boolean;
  noWatchers: boolean;
  signs: number;
};

export type Invites = {
  [key: string]: ITable;
};

export interface IInviteService {
  onInvitesChange: Subject<Invites>;
  doNotDisturb: boolean;

  onGotInvite(invite: { tid: number; type: number }): void;
  addInvite(table: ITable): void;
  checkInvite(table: ITable): boolean;
  getInvites(): ITable[];
  removeInvites(table: ITable | undefined): void;
  sendInvite(pid: number, tid: number, inviteType: number): void;
  sendDeclineInvite(table: ITable): void;
  declineAllInvites(): void;
  declineInvite(tableId: number): void;
  acceptInvite(tableId: number, eulaAccepted: boolean): boolean;
  getDisturbStatus(): boolean;
  setDisturbStatus(status: boolean): void;
}

export enum ConnectionType {
  CONNECT_TOURNEY_ROOM = 'CONNECT_TOURNEY_ROOM',
  COMEBACK_LOBBY = 'COMEBACK_LOBBY',
  CONNECT_DEFAULT = 'CONNECT_DEFAULT',
}

export type GameProgress = {
  status: number;
  tid: number;
  turn: number;
  times: number[];
  moves: string;
};

export type ModalButton = {
  additionalCases?: string[];
  title: string;
  onClick: () => void;
};
export interface ModalObservable {
  title?: string;
  body: ReactNode;
  classes?: {
    body?: string;
    modal?: string;
    title?: string;
    footer?: string;
  };
  buttons?: ModalButton[];
  headerBtn?: boolean;
  closeModal?: () => void;
}

export type Scores = {
  type: number;
  tid: number;
  score: number[];
};
export interface Clock {
  onClockTick: BehaviorSubject<number>;
  stopClock(): void;
  startClock(color: Color, restart: boolean): void;
  getElapsedTime(color: Color): number;
}

export enum GameButton {
  ROLL,
  DONE,
  UNDO,
  INVITE,
  CONTINUE,
  START,
  NEW_GAME,
  SHOW_DOUBLE_DIALOG,
  CONFIRM,
  KNOCK,
  PASS,
  CANCEL
}

export interface BaseGameState {
  onScoresReceived(scores: any): void;
  onUrgeRecieved(): void;
  onTurnChanged: Subject<TableSides>;
  opponentIdleActionsDialog: OpponentIdleActionsDialog;
  onStatusMessagesChanged: BehaviorSubject<string>;
  onGameStarted: Subject<boolean>;
  onGameFinished: Subject<boolean>;
  onRoundStarted: Subject<boolean>;
  onRoundFinished: Subject<boolean>;
  onScoreChanged: Subject<boolean>;
  onPlayersCountChanged: Subject<number>;
  onMoveTimeChanged: Subject<boolean>;
  clock: Clock;
  currentTurn?: TableSides;
  me: IPlayer;
  gameInProgress: BehaviorSubject<boolean>;
  roundInProgress: BehaviorSubject<boolean>;
  getTable(): ITable;
  getMyColor(): Color;
  setStatusMessage(statusMessage: string, restoreMessageTimeout?: number): void;
  imWatching(): boolean;
  imPlaying(): boolean;
  isMyTurn(): boolean;
  isGameInProgress(): boolean;
  loadGame(status: number, turn: number, times: Array<any>, moves: string): void;
  onActionReceived(action: any): void;
  onPlayerJoined(player: IPlayer): void;
  onPlayerLeft(player: IPlayer): void;
  onDealReceived(deal: any): any;
  onMoveReceived(move: any): void;
  onShutdown(): void;
  startClick(): void;
  continueClick(): void;
  newGameClick(): void;
  shouldUrgeOtherPlayer(): boolean;
  onReconnectionReceieved(command: any, player: IPlayer, par: number): void;
  getColorForSide(side: TableSides): Color;
  onGameButtonsChanged: BehaviorSubject<GameButton[]>;
  getBlackPlayer(): IPlayer | undefined;
  getWhitePlayer(): IPlayer | undefined;
}

export interface ITable {
  tid: number;
  inviteOnly: boolean;
  cost: number;
  timePerMove: number;
  maxPoints: number;
  minPoints: number;
  ladder: boolean;
  noWatchers: boolean;
  seats: (IPlayer | undefined)[];
  owner?: IPlayer;
  gameState?: BaseGameState;
  clearChatMessages(): void;
  getMaxPlayersCount(): number;
  getPlayerBySeat(seat: number): IPlayer | undefined;
  setPlayer(player: IPlayer | undefined, seat: number): void;
  swapPlayers(p1: IPlayer, p2: IPlayer): void;
  getOwner(): IPlayer | undefined;
  canBePlayed(): boolean;
  canBeWatched(): boolean;
  setOwner(player: IPlayer): void;
  getWhitePlayer(): IPlayer;
  getBlackPlayer(): IPlayer;
  isAllPlaying(): boolean;
  getCountOfPlayers(excludeBots?: boolean): number;
  hasPlayers(): boolean;
  hasPeople(): boolean;
  getNumber(): number;
  getGameState(): BaseGameState;
  setGameState(gameState: BaseGameState): void;
  getCost(): number;
  getTimePerMoveString(time: number, postfix: string): string;
  getMatchStringLong(): string;
  getMatchString(): string;
  resetGameState(): void;
  getTicketString(): string;
  showProjectedRating(me: IPlayer, opponent: IPlayer | undefined): void;
  isPhantomTable(): boolean;
  getAnyPlayer(): IPlayer;
  getPlayers(): IPlayer[];
  getCurrentTurnPlayer(): IPlayer | undefined;
  getPlayerByColor(color: Color): IPlayer | undefined;
  addChatMessage(msg: Message): void;
  chatMessages: Message[];
  onChatMessages: BehaviorSubject<Message[]>;
}

export type ReceiveMessageType = {
  type: number;
  pid: number;
  tid: number;
  text: string;
  color: string;
};

export type CreateMessageType = {
  type: number;
  tid?: number;
  pid?: number;
  text: string;
  color?: string;
  recipientName?: string;
};
